import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useState } from 'react';
import EffectiveDateField from '../common/EffectiveDateField';
import CurrencyField from '../../benefit/fields/CurrencyField';
import { FormValidationContext } from '../../benefit/common/FormValidationContext';
import { useHydratedForm } from '../hooks/useHydratedForm';
import { useBootstrapValidation } from '../hooks/userBootstrapValidation';
import { useUI } from '../../../context/UIContext';
import { hasMeaningfulChange } from '../../../utils/hasMeaningfulChange';
import { calculatorService } from '../../../services/calculatorService';

const mapFplSmiAdderToForm = (fplSmiAdder) => ({
  id: fplSmiAdder.id,
  fplAdderPerHousehold: fplSmiAdder.fplAdderPerHousehold ?? '',
  smiAdderPerHousehold: fplSmiAdder.smiAdderPerHousehold ?? '',
  effectiveDate: fplSmiAdder.effectiveDate ?? '',
});

const MEANINGFUL_FIELDS = ['fplAdderPerHousehold', 'smiAdderPerHousehold', 'effectiveDate'];

const EditFplSmiAdderModal = ({ fplSmiAdder, onSave, onClose }) => {
  const [form, setForm] = useHydratedForm(fplSmiAdder, mapFplSmiAdderToForm);

  const { validated, validateForm } = useBootstrapValidation();
  const [customError, setCustomError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState('');
  const { showLoading, hideLoading, showError, showSuccess } = useUI();

  if (!fplSmiAdder) return null;

  const handleSubmit = async (e) => {
    const formEl = e.currentTarget;
    e.preventDefault();

    if (!hasMeaningfulChange(fplSmiAdder, form, MEANINGFUL_FIELDS)) {
      setCustomError(
        'At least one value must be changed before saving.'
      );
      return; 
    }

    if (!validateForm(formEl)) return;

    setIsSubmitting(true);
    showLoading('Saving FPL & SMI Adder configuration…');

    try {
      const response = await calculatorService.saveFplSmiAdder(form);
      setIsSubmitting(false);

      if (response.success) {
        onSave(response.data);
        onClose();
        showSuccess("FPL & SMI Adder configuration saved.")
      } else {
        showError(
          response.error || 'Failed to save tier configuration.'
        );
      }
    } finally {
      setIsSubmitting(false);
      hideLoading();
    }
  };

  return (
    <Modal show onHide={onClose} size="lg" centered>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <FormValidationContext.Provider value={validated}>
          <Modal.Header closeButton>
            <Modal.Title>
              Edit FPL / SMI Household Adder
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
                <Row className="mb-3">
                  <Col>
                    <CurrencyField
                      value={form.fplAdderPerHousehold}
                      scale={0}
                      fieldName="FPL Adder (per additional household)"
                      required
                        onChange={(value) =>
                          setForm({ ...form, fplAdderPerHousehold: value })
                        }
                      isSubmitting={isSubmitting}
                    />
                  </Col>

                  <Col>
                    <CurrencyField
                      value={form.smiAdderPerHousehold}
                      scale={0}
                      fieldName="SMI Adder (per additional household)"
                      required
                        onChange={(value) =>
                          setForm({ ...form, smiAdderPerHousehold: value })
                        }
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <EffectiveDateField
                      value={form.effectiveDate}
                      onChange={(v) => setForm({ ...form, effectiveDate: v })}
                      isSubmitting={isSubmitting}
                    />
                  </Col>
                </Row>

                {customError && (
                  <div className="text-center text-danger mt-3">
                    {customError}
                  </div>
                )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Save
            </Button>
          </Modal.Footer>
        </FormValidationContext.Provider>
      </Form>
    </Modal>
  );
};

export default EditFplSmiAdderModal;